<template>
	<Modal v-if="shown"
		   :title="isMultipleRows ? $t(titleMultiKey) : $t(titleSingleKey)"
		   @close="show(false)"
		   :close-on-click-outside="false"
		   :width="500">
		<template v-slot:body>
			<div class="text_left">
				<span class="text_block margin_bottom" v-html="isMultipleRows
				  ? $t(msgMultiKey, { count: multipleRows.length })
				  : $t(msgSingleKey)
				"></span>

				<form>
					<div class="margin_bottom" v-if="includeReason">
						<md-field>
							<md-textarea
								class="textarea note-textarea base_padding"
								v-model="form.reason"
								md-autogrow
							/>
						</md-field>
					</div>

					<div v-if="companiesWithDifferentEvaluationStatus.length > 0">
						<span class="text_block">
							{{ $t(evaluationMsgKey) }}
						</span>
						<div class="margin_top">
							<span class="text_block"
								  v-if="companiesWithSameEvaluationStatus.length === 1"
								  v-html="$t(evaluationMatchingDecisionMsgSingleKey, { companyName: companiesWithSameEvaluationStatus[0].name })"
							></span>
							<span class="text_block"
								  v-if="companiesWithSameEvaluationStatus.length > 1"
								  v-html="$t(evaluationMatchingDecisionMsgMultipleKey, { count: companiesWithSameEvaluationStatus.length })"
							></span>
							<span class="text_block"
								  v-if="companiesWithOppositeEvaluationStatus.length === 1"
								  v-html="$t(evaluationNotMatchingDecisionMsgSingleKey, { companyName: companiesWithOppositeEvaluationStatus[0].name })"
							></span>
							<span class="text_block"
								  v-if="companiesWithOppositeEvaluationStatus.length > 1"
								  v-html="$t(evaluationNotMatchingDecisionMsgMultipleKey, { count: companiesWithOppositeEvaluationStatus.length })"
							></span>
						</div>

						<MarketSoundingDecisionCompanyEvaluation
							:sellers="uniqueSellersWithDifferentEvaluationStatus"
							:debtors="uniqueDebtorsWithDifferentEvaluationStatus"
							:evaluation-data="form.companyEvaluation"
							:evaluation-preference-key="evaluationPreferenceKey"
							:evaluate-label-key="evaluateLabelKey"
							:evaluate-not-label-key="evaluateNotLabelKey"
							:max-company-list-height="maxCompanyListHeight"
							:validator="$validator"
							ref="companyEvaluation"
						/>
					</div>
				</form>

				<div class="flex flex_space">
					<md-button @click="close" class="md-blank">
						{{ $t('common.button.close') }}
					</md-button>
					<md-button @click="submit" :class="{ 'md-primary': isFormValid }">
						{{ $t('common.button.continue') }}
					</md-button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from "@/components/templates/modal/Modal";
import MarketSoundingDecisionCompanyEvaluation
	from "src/components/templates/investor/riskAssessment/marketSounding/MarketSoundingDecisionCompanyEvaluation.vue";
import {validateForm, validateRequiredFields} from "@/validation/validation";
import {mapGetters} from "vuex";
import {enumDef as enumDefs} from "@/common/enumDefs";
import {concat} from "loadsh/array";

export default {
	name: 'MarketSoundingDecisionConfirmModal',
	components: { Modal, MarketSoundingDecisionCompanyEvaluation },
 	props: {
		titleSingleKey: { type: String, required: true },
		titleMultiKey: { type: String, required: true },
		msgSingleKey: { type: String, required: true },
		msgMultiKey: { type: String, required: true },
		evaluationStatus: { type: Number, required: true },
		evaluationMsgKey: { type: String, required: true },
		evaluationMatchingDecisionMsgSingleKey: { type: String, required: true },
		evaluationMatchingDecisionMsgMultipleKey: { type: String, required: true },
		evaluationNotMatchingDecisionMsgSingleKey: { type: String, required: true },
		evaluationNotMatchingDecisionMsgMultipleKey: { type: String, required: true },
		evaluationPreferenceKey: { type: String, required: true },
		evaluateLabelKey: { type: String, required: true },
		evaluateNotLabelKey: { type: String, required: true },
		maxCompanyListHeight: { type: Number, default: 300 },
		includeReason: { type: Boolean, default: false },
	},
	data() {
		let defaultForm = {
			reason: null,
			companyEvaluation: {
				evaluateCompanies: null,
				evaluateSellerIds: [],
				evaluateDebtorIds: []
			}
		};

		return {
			shown: false,
			singleRowData: null,
			multipleRows: null,
			defaultForm: defaultForm,
			form: { ...defaultForm },
			modelValidations: {
				reason: {
					required: false
				},
				// Evaluation validation is dynamically embedded
			}
		};
	},
	computed: {
		...mapGetters({
			investorPreferences: "local/investorPreferences",
		}),
		rows() {
			return this.multipleRows || (this.singleRowData ? [this.singleRowData] : []);
		},
		isMultipleRows() {
			return this.multipleRows != null;
		},
		isFormValid() {
			let flattenedForm = { ...this.form, ...this.form.companyEvaluation };

			return validateRequiredFields(flattenedForm, this.modelValidations);
		},
		uniqueSellers() {
			return this.buildUniqueCompaniesSet("sellerId", "sellerName", "investorSellerConfigStatus");
		},
		uniqueDebtors() {
			return this.buildUniqueCompaniesSet("debtorId", "debtorName", "investorDebtorConfigStatus");
		},
		companiesWithSameEvaluationStatus() {
			let uniqueSellersWithSameEvaluationStatus = this.uniqueSellers.filter(s => s.evaluationStatus === this.evaluationStatus);
			let uniqueDebtorsWithSameEvaluationStatus = this.uniqueDebtors.filter(d => d.evaluationStatus === this.evaluationStatus);

			return uniqueSellersWithSameEvaluationStatus.concat(uniqueDebtorsWithSameEvaluationStatus);
		},
		uniqueSellersWithDifferentEvaluationStatus() {
			return this.uniqueSellers.filter(s => s.hasDifferentEvaluationStatus);
		},
		uniqueDebtorsWithDifferentEvaluationStatus() {
			return this.uniqueDebtors.filter(d => d.hasDifferentEvaluationStatus);
		},
		companiesWithDifferentEvaluationStatus() {
			return this.uniqueSellersWithDifferentEvaluationStatus.concat(this.uniqueDebtorsWithDifferentEvaluationStatus);
		},
		companiesWithOppositeEvaluationStatus() {
			return this.companiesWithDifferentEvaluationStatus.filter(c => c.isEvaluated);
		},
	},
	methods: {
		show(value) {
			this.shown = value;
		},
		showWithSingleRow(data) {
			this.show(true);

			this.singleRowData = data;
			this.multipleRows = null;

			this.initForm();
		},
		showWithMultipleRows(rows) {
			this.show(true);

			this.singleRowData = null;
			this.multipleRows = rows;

			this.initForm();
		},
		initForm() {
			this.form = {
				...this.defaultForm,
				companyEvaluation: {
					...this.defaultForm.companyEvaluation,
					evaluateSellerIds: [], // Create a new array here, because just a reference would alter the default one
					evaluateDebtorIds: [], // Create a new array here, because just a reference would alter the default one
				}
			};

			// Merge with evaluation validations after the evaluation ref is rendered
			this.$nextTick(() => {
				this.modelValidations = {
					...this.modelValidations,
					...this.$refs.companyEvaluation.modelValidations
				};
			});
		},
		close() {
			this.show(false);
		},
		submit() {
			validateForm(this, this.$validator)
				.then(() => {
					this.$emit('confirm', this.rows, this.form.reason, this.form.companyEvaluation);

					this.show(false);

					// Save evaluation decision in local preferences
					this.$store.dispatch("local/setInvestorPreferences", {
						marketSounding: {
							...this.investorPreferences.marketSounding,
							[this.evaluationPreferenceKey]: this.form.companyEvaluation.evaluateCompanies,
						},
					});
				})
				.catch(this.toast);
		},
		buildUniqueCompaniesSet(idField, nameField, evaluationStatusField) {
			const map = new Map();

			this.rows.forEach(r => {
				if (!map.has(r[idField])) {
					map.set(r[idField], {
						id: r[idField],
						name: r[nameField],
						evaluationStatus: r[evaluationStatusField],
						hasDifferentEvaluationStatus: r[evaluationStatusField] !== this.evaluationStatus,
						isEvaluated: enumDefs.InvestorSellerDebtorConfigStatus.isEvaluated(r[evaluationStatusField]),
					});
				}
			});

			return Array.from(map.values());
		}
	},
};
</script>
